import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import './NarrativeFeed.css';

function WordReveal({ html, delay = 0 }) {
  const [visibleWords, setVisibleWords] = useState(0);
  const containerRef = useRef(null);

  // Parse HTML to extract words while preserving tags
  const parseHtml = (htmlString) => {
    const temp = document.createElement('div');
    temp.innerHTML = DOMPurify.sanitize(htmlString);
    const words = [];

    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        const splitWords = text.split(/(\s+)/);
        splitWords.forEach(word => {
          if (word.trim()) {
            words.push({ type: 'text', content: word });
          } else if (word) {
            words.push({ type: 'space', content: word });
          }
        });
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        const className = node.className;
        const innerWords = [];

        node.childNodes.forEach(child => {
          const childWords = [];
          if (child.nodeType === Node.TEXT_NODE) {
            const text = child.textContent;
            const splitWords = text.split(/(\s+)/);
            splitWords.forEach(word => {
              if (word.trim()) {
                childWords.push(word);
              } else if (word) {
                childWords.push(word);
              }
            });
          }
          innerWords.push(...childWords);
        });

        innerWords.forEach((word, i) => {
          if (word.trim()) {
            words.push({
              type: 'element',
              tagName,
              className,
              content: word
            });
          } else {
            words.push({ type: 'space', content: word });
          }
        });
      }
    };

    temp.childNodes.forEach(processNode);
    return words;
  };

  const words = parseHtml(html);
  const actualWords = words.filter(w => w.type !== 'space');
  const totalWords = actualWords.length;

  useEffect(() => {
    setVisibleWords(0);
    const startTimeout = setTimeout(() => {
      const interval = setInterval(() => {
        setVisibleWords(prev => {
          if (prev >= totalWords) {
            clearInterval(interval);
            return prev;
          }
          return prev + 1;
        });
      }, 35); // Speed of word reveal

      return () => clearInterval(interval);
    }, delay);

    return () => clearTimeout(startTimeout);
  }, [html, delay, totalWords]);

  const renderWords = () => {
    let wordIndex = 0;
    return words.map((word, i) => {
      if (word.type === 'space') {
        return <span key={i}>{word.content}</span>;
      }

      const isVisible = wordIndex < visibleWords;
      wordIndex++;

      if (word.type === 'element') {
        return (
          <span
            key={i}
            className={`word-reveal ${word.className} ${isVisible ? 'visible' : ''}`}
          >
            {word.content}
          </span>
        );
      }

      return (
        <span
          key={i}
          className={`word-reveal ${isVisible ? 'visible' : ''}`}
        >
          {word.content}
        </span>
      );
    });
  };

  return (
    <p ref={containerRef} className="paragraph word-reveal-paragraph">
      {renderWords()}
    </p>
  );
}

function NarrativeFeed({ narrative, showSentences, hideTitle, onBack }) {
  if (!narrative) return null;

  // Calculate cumulative delays based on word count of previous paragraphs
  const getWordCount = (html) => {
    const temp = document.createElement('div');
    temp.innerHTML = DOMPurify.sanitize(html);
    const text = temp.textContent || temp.innerText || '';
    return text.split(/\s+/).filter(w => w.trim()).length;
  };

  let cumulativeDelay = 0;
  const paragraphDelays = showSentences.map((paragraph, index) => {
    const delay = cumulativeDelay;
    const wordCount = getWordCount(paragraph);
    cumulativeDelay += wordCount * 35 + 200; // 35ms per word + 200ms pause between paragraphs
    return delay;
  });

  return (
    <div className="narrative-feed">
      {!hideTitle && (
        <h2 className="narrative-title">
          {onBack && (
            <button className="narrative-back-button" onClick={onBack}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          {narrative.title}
        </h2>
      )}
      {!hideTitle && narrative.meta && (
        <p className="narrative-meta">{narrative.meta}</p>
      )}

      <div className="sentences-container">
        {showSentences.map((paragraph, index) => (
          <WordReveal
            key={index}
            html={paragraph}
            delay={paragraphDelays[index]}
          />
        ))}
      </div>
    </div>
  );
}

export default NarrativeFeed;
